/* eslint-disable camelcase, no-param-reassign, max-lines-per-function, max-params, max-lines */

import { api } from "jiffy-api";
import { track, registerMixpanel } from "jiffy-analytics";
import "./product-details-delivery-date-with-current-cart.scss";
import { EventBus, Events } from "jiffy-store";
import createTimerManager from "common/utils/delivery-date/make-timer-manager";
import {
  normalizeCompositionName,
  resolveCompositionName
} from "common/utils/delivery-date/resolve-composition-name";
import insertDebugEstimation from "./product-details-delivery-date-with-current-cart-debug/product-details-delivery-date-with-current-cart-debug";
import { submitErrorGA, submitSuccessGA } from "../deliver-to/deliver-to-ga-events";
import setPDPDatesLoading from "./product-details-delivery-date-with-current-cart_loading";
import insertDeliveryDates, {
  trackProductViewed
} from "./product-details-delivery-date-with-current-cart_insert-dates";

const MODIFIERS = {
  SINGLE_SHIPMENT: "product-details-delivery-date-with-current-cart__delivery--single",
  MULTIPLE_SHIPMENT: "product-details-delivery-date-with-current-cart__delivery--multiple"
};

const SELECTORS = {
  CONTAINER: ".js-product-details-delivery-dates-container",
  CUTOFF: ".js-product-details-delivery-dates-cutoff",
  SPINNER: ".js-product-details-delivery-dates-spinner",
  ACTIVE_COLOR_NAME: "#active-color-name",
  COLOR_VARIANT_SELECTOR: ".js-color-variant-selector",
  // Delivery dates variants:
  DATES_SHIPMENT: ".js-product-details-delivery-dates-shipment",
  CART_SHIPMENT: ".js-product-details-delivery-dates-cart-shipment",
  CART_SEPARATE_SHIPMENT: ".js-product-details-delivery-dates-cart-separate-shipment",
  FORM: ".js-delivery-date-form",
  FORM_INPUT: ".js-delivery-date-form-input",
  FORM_ERROR: ".js-delivery-date-form-error",
  CART_DELIVERY_DATE: ".js-product-details-delivery-dates-cart-value",
  DIRECT_DELIVERY_DATE: ".js-product-details-delivery-dates-direct-value"
};

const { makeTimer, clearTimers } = createTimerManager();

const isActiveProductAssignable = () => {
  const { value: color } = document.querySelector(SELECTORS.ACTIVE_COLOR_NAME);
  const $colorVariantSelector = document.querySelector(
    `${SELECTORS.COLOR_VARIANT_SELECTOR}[data-color="${color}"]`
  );

  if (!$colorVariantSelector) {
    return false;
  }
  return $colorVariantSelector.hasAttribute("data-show-estimates");
};

const showCorrectDates = ({
  cart_dates: { only_cart_shipments: onlyCartShipments },
  composition_dates: compositionDates
}) => {
  const daysCount = compositionDates.size;
  const $datesShipment = document.querySelector(SELECTORS.DATES_SHIPMENT);
  const $cartShipment = document.querySelector(SELECTORS.CART_SHIPMENT);
  const $cartSeparateShipment = document.querySelector(SELECTORS.CART_SEPARATE_SHIPMENT);
  const cartShipmentsLength = onlyCartShipments?.length || 0;

  if (cartShipmentsLength) {
    $datesShipment.classList.add("hidden");
  } else {
    $datesShipment.classList.remove("hidden");
    $datesShipment.classList.toggle(MODIFIERS.SINGLE_SHIPMENT, daysCount === 1);
    $datesShipment.classList.toggle(MODIFIERS.MULTIPLE_SHIPMENT, daysCount > 1);
  }

  $cartShipment.classList.add("hidden");
  $cartSeparateShipment.classList.toggle("hidden", cartShipmentsLength <= 0);
};

const fetchDeliveryDate = async (productId, compositionName, payload) => {
  if (!isActiveProductAssignable()) return {};

  const normalizedCompositionName = normalizeCompositionName(compositionName);
  if (!productId || !normalizedCompositionName) return {};

  const $container = document.querySelector(SELECTORS.CONTAINER);
  const data = await api.product.fetchDeliveryEstimatesForCurrentCart(
    { productId },
    { ...payload, composition_name: normalizedCompositionName }
  );

  const activeColor = document.querySelector(SELECTORS.ACTIVE_COLOR_NAME)?.value;
  const normalizedActiveColor = normalizeCompositionName(activeColor);
  if (normalizedActiveColor === normalizedCompositionName) {
    $container.classList.toggle("hidden", !data.success);
  }

  return data.success ? data : {};
};

const updateHTML = (deliveryDates, shouldTrack = true) => {
  const $container = document.querySelector(SELECTORS.CONTAINER);
  const { debug } = $container.dataset;

  // Debug mode is loaded special debug component
  if (debug === "true") {
    insertDebugEstimation(deliveryDates);
    return;
  }

  const { cart_dates, composition_dates } = deliveryDates;
  const $cutoffCollection = document.querySelectorAll(SELECTORS.CUTOFF);
  if (!cart_dates?.timeleft && !composition_dates?.timeleft) return;

  const cutoffSource =
    cart_dates.shipments?.length > 0 ? cart_dates.timeleft : composition_dates.timeleft;
  const getCutoffTime = new Date(cutoffSource).getTime();

  showCorrectDates(deliveryDates);
  insertDeliveryDates(deliveryDates);
  if (shouldTrack) {
    trackProductViewed(deliveryDates);
  }
  clearTimers();
  $cutoffCollection.forEach($cutoff => {
    makeTimer($cutoff, getCutoffTime);
  });
};

const hasCartDeliveryDateChanged = (
  previousCartDeliveryDateText,
  { value_full: currentCartDeliveryDate }
) => {
  if (currentCartDeliveryDate) {
    return previousCartDeliveryDateText !== currentCartDeliveryDate;
  }
  return false;
};

const hasDirectDeliveryDateChanged = (
  previousDirectDeliveryDateText,
  { value_full: currentDirectDeliveryDate }
) => {
  if (currentDirectDeliveryDate) {
    return previousDirectDeliveryDateText !== currentDirectDeliveryDate;
  }
  return false;
};

const sendPDPDeliveryDateChangeTracking = (
  previousCartDeliveryDate,
  previousDirectDeliveryDate,
  { cart_dates, composition_dates, mixpanel }
) => {
  if (
    mixpanel &&
    (hasCartDeliveryDateChanged(previousCartDeliveryDate.text, cart_dates) ||
      hasDirectDeliveryDateChanged(previousDirectDeliveryDate.text, composition_dates))
  ) {
    const deliveryDatePayload = {
      previous_with_cart_delivery_date: previousCartDeliveryDate.date,
      previous_separately_shipped_delivery_date: previousDirectDeliveryDate.date,
      ...mixpanel
    };
    track("pdp_delivery_date_changed", deliveryDatePayload);
  }
};

/**
 * The function provides new dates after changing color in the color picker popup or carousel.
 * @param productId {string}
 * @param compositionName {string}
 * @param orderNumber {string}
 */
const loadDeliveryEstimates = async (productId, compositionName, orderNumber = "") => {
  const normalizedCompositionName = normalizeCompositionName(compositionName);

  setPDPDatesLoading(true);
  const payload = { composition_name: normalizedCompositionName };
  if (orderNumber) {
    payload.number = orderNumber;
  }
  const deliveryDates = await fetchDeliveryDate(productId, normalizedCompositionName, payload);
  setPDPDatesLoading(false);
  updateHTML(deliveryDates, !orderNumber);
};

/**
 * The function provides new dates after updating qty inputs below the delivery dates box.
 * @param productId {string}
 * @param compositionName {string}
 * @param variantString {string}
 * @param orderNumber {string}
 */
const loadDeliveryEstimatesForVariant = async (
  productId,
  compositionName,
  variantString,
  orderNumber = ""
) => {
  const normalizedCompositionName = normalizeCompositionName(compositionName);

  const $directDeliveryValue = document.querySelector(SELECTORS.DIRECT_DELIVERY_DATE);
  const currentDirectDeliveryDate = {
    text: $directDeliveryValue?.textContent,
    date: $directDeliveryValue?.dataset?.deliveryDate
  };

  const $cartDeliveryValue = document.querySelector(SELECTORS.CART_DELIVERY_DATE);
  const currentCartDeliveryDate = {
    text: $cartDeliveryValue?.textContent,
    date: $cartDeliveryValue?.dataset?.deliveryDate
  };

  setPDPDatesLoading(true);
  const payload = { composition_name: normalizedCompositionName, variants_hash: variantString };
  if (orderNumber) {
    payload.number = orderNumber;
  }
  const deliveryDates = await fetchDeliveryDate(productId, normalizedCompositionName, payload);
  setPDPDatesLoading(false);
  updateHTML(deliveryDates, !orderNumber);
  sendPDPDeliveryDateChangeTracking(
    currentCartDeliveryDate,
    currentDirectDeliveryDate,
    deliveryDates
  );
};

const initDates = async $container => {
  const { productId } = $container.dataset;
  let { selectedColor, selectedCart } = $container.dataset;
  selectedColor = resolveCompositionName({
    containerSelectedColor: selectedColor,
    activeColorName: document.querySelector(SELECTORS.ACTIVE_COLOR_NAME)?.value,
    preferQueryParam: true
  });
  if (selectedColor) {
    $container.dataset.selectedColor = selectedColor;

    // Waiting for dates and insert them to the html component
    await loadDeliveryEstimates(productId, selectedColor);
  }

  // After selecting new color in the color picker, we should refresh estimation dates
  EventBus.on(Events.PRODUCT_COLOR_SELECTED, async (evt, { color }) => {
    const normalizedSelectedColor = normalizeCompositionName(color);
    if (!normalizedSelectedColor) return;

    selectedColor = normalizedSelectedColor;
    $container.dataset.selectedColor = normalizedSelectedColor;
    await loadDeliveryEstimates(productId, selectedColor);
  });

  // After selecting saved cart, we should refresh estimation dates
  EventBus.on(Events.SAVED_CART_SELECTED, async (evt, { number }) => {
    selectedCart = number;
    if (!selectedColor) return;
    await loadDeliveryEstimates(productId, selectedColor, selectedCart);
  });

  // Depends of count of goods in order delivery dates could be shifted. Checking this case:
  EventBus.on(Events.PRODUCT_INPUT_CHANGED, async ({ target: form }, totalValue, colorName, _) => {
    const payload = Array.from(form.getElementsByClassName("js-quantity-form-input")).reduce(
      (acc, input) => {
        const quantity = input.value;
        const { variantId } = input.dataset;
        return { ...acc, [variantId]: acc[variantId] || quantity };
      },
      {}
    );
    await loadDeliveryEstimatesForVariant(
      productId,
      colorName,
      JSON.stringify(payload),
      selectedCart
    );
  });
};

const initForm = $container => {
  const $form = document.querySelector(SELECTORS.FORM);
  const $formInput = $form.querySelector(SELECTORS.FORM_INPUT);
  const $error = $form.querySelector(SELECTORS.FORM_ERROR);
  const $spinner = document.querySelector(SELECTORS.SPINNER);

  const toggleFormLoading = isLoading => {
    if (isLoading) {
      $form.classList.toggle("hidden", isLoading);
      $spinner.classList.toggle("shown", isLoading);
    } else {
      $form.classList.toggle("hidden", isLoading);
      $spinner.classList.toggle("shown", isLoading);
    }
  };

  const showError = message => {
    const msg = message !== undefined ? message : "Please enter a valid zipcode.";
    $error.innerHTML = msg;
    toggleFormLoading(false);
    submitErrorGA("zipcode");
  };

  const trackPDPZipcode = zipcode => {
    const $productData = document.getElementById("product-page-container");
    const $compositionData = document.querySelector(".product-colors-slider__item-link--active");
    const $compositionNameData = $compositionData?.querySelector("[data-composition-name]");

    if ($productData && $compositionNameData) {
      const { realProductId: productId, productId: productName } = $productData.dataset;
      const { compositionId } = $compositionData.dataset;
      const { compositionName } = $compositionNameData.dataset;

      if (productId && compositionName) {
        track("product_detail_zipcode", {
          product_id: parseInt(productId, 10),
          spree_composition_id: parseInt(compositionId, 10),
          composition: compositionName,
          zipcode,
          product: productName
        });
      }
    }
  };

  // After selecting new color in the color picker, we should update container's data
  EventBus.on(Events.PRODUCT_COLOR_SELECTED, async (evt, { color }) => {
    $container.dataset.selectedColor = color;
  });
  // After selecting saved cart, we should update container's data
  EventBus.on(Events.SAVED_CART_SELECTED, async (evt, { number }) => {
    $container.dataset.selectedCart = number;
  });

  $form.addEventListener("submit", async e => {
    e.preventDefault();
    toggleFormLoading(true);

    api.jiffyshirts.locations
      .create({
        zipcode: $formInput.value
      })
      .then(data => {
        if (!data.error) {
          const { cities, zipcode } = data;
          const city = Array.isArray(cities) ? cities[0].name : "";

          // send analytics data
          submitSuccessGA("zipcode");
          // send mixpanel data
          registerMixpanel({ zipcode });
          trackPDPZipcode(zipcode);
          // fill related places with selected address
          window.JiffyDeliverTo.updateZipCodeInHeader({ zipcode, city });
          // hide form
          $form.classList.add("hidden");
          // init delivery dates section
          initDates($container);
        } else if (data.error) {
          showError(data.message);
        } else {
          showError();
        }
      })
      .catch(() => {
        const message = "Something went wrong. Please try again later.";
        showError(message);
      });
  });
};

export default async (zipcode, isAddressExists) => {
  const $container = document.querySelector(SELECTORS.CONTAINER);
  if (!$container) return;

  const $form = document.querySelector(SELECTORS.FORM);
  const $spinner = document.querySelector(SELECTORS.SPINNER);

  if (isAddressExists || zipcode) {
    initDates($container);
  } else {
    initForm($container);
    $spinner?.classList.remove("shown");
    $form?.classList.remove("hidden");
  }

  // manage component visibility according to the `showEstimates` composition state
  EventBus.on(Events.PRODUCT_COLOR_SELECTED, (_evt, { _color, showEstimates }) => {
    $container.classList.toggle("hidden", showEstimates === "false");
  });
};
